import 'dayjs/locale/lt';

import { Box, useTheme } from '@mui/material';
import {
 CategoryScale,
 Chart,
 Chart as ChartJS,
 Legend,
 LinearScale,
 LineElement,
 PointElement,
 Title,
 Tooltip,
} from 'chart.js';
import dayjs from 'dayjs';
import React, { useCallback, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { WidgetItemType } from '../Dashboard.types';

ChartJS.register(
 CategoryScale,
 LinearScale,
 PointElement,
 LineElement,
 Title,
 Tooltip,
 Legend,
);

interface DashboardWidgetLineChartProps {
 widgetContent: WidgetItemType[];
}

const DashboardWidgetLineChart = ({
 widgetContent,
}: DashboardWidgetLineChartProps) => {
 const { t } = useTranslation();
 const theme = useTheme();

 const chartData = useMemo(() => {
  if (!widgetContent || widgetContent.length === 0) {
   return null;
  }

  // Extract unique dates from all items
  const allDates = new Set<string>();
  widgetContent.forEach((item) => {
   item.Items.forEach((entry) => {
    if (entry.Date) {
     const dateStr = dayjs(entry.Date).format('MM-DD');
     allDates.add(dateStr);
    }
   });
  });

  const sortedDates = Array.from(allDates).sort();

  // Create datasets for each group (Key)
  const datasets = widgetContent.map((item, index) => {
   // Use MUI theme palette for colors
   // We'll rotate through primary, secondary, error, warning, info, success for distinct datasets
   const chartColors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.error.main,
    // theme.palette.secondary.main,
   ];

   const color = chartColors[index % chartColors.length];

   // Map dates to values for this dataset
   const data = sortedDates.map((date) => {
    const matchingItem = item.Items.find((entry) => {
     if (!entry.Date) return false;
     const entryDate = dayjs(entry.Date).format('MM-DD');
     return entryDate === date;
    });

    return matchingItem?.Neto ?? 0;
   });

   let label = item.Key?.replace('(null)', t('global.unassigned'));

   return {
    label: label,
    data,
    borderColor: color,
    backgroundColor: `${color}40`,
    borderWidth: 1,
    borderDash: [6, 4],
    tension: 0.1,
   };
  });

  return {
   labels: sortedDates,
   datasets,
  };
 }, [theme, widgetContent]);

 if (!chartData) {
  return null;
 }

 const getOrCreateTooltip = useCallback((chart: any) => {
  let tooltipEl = chart.canvas.parentNode.querySelector(
   '[data-chart-tooltip="line-widget"]',
  );

  if (!tooltipEl) {
   tooltipEl = document.createElement('div');
   tooltipEl.dataset.chartTooltip = 'line-widget';
   tooltipEl.style.pointerEvents = 'none';
   tooltipEl.style.position = 'absolute';
   tooltipEl.style.transform = 'translate(-50%, calc(-100% - 8px))';
   tooltipEl.style.transition = 'all .1s ease';
   tooltipEl.style.zIndex = '10';
   tooltipEl.style.borderRadius = '4px';
   tooltipEl.style.padding = '8px 12px';
   tooltipEl.style.boxShadow = '0px 4px 12px 0px rgba(0, 0, 0, 0.08)';
   tooltipEl.style.minWidth = '120px';
   tooltipEl.style.opacity = '0';

   const table = document.createElement('table');
   table.style.margin = '0px';
   table.style.borderCollapse = 'collapse';
   table.style.width = '100%';

   tooltipEl.appendChild(table);
   chart.canvas.parentNode.appendChild(tooltipEl);
  }

  return tooltipEl;
 }, []);

 const externalTooltipHandler = useCallback(
  (context: any) => {
   const { chart, tooltip } = context;
   const tooltipEl = getOrCreateTooltip(chart);
   const isDarkMode = theme.palette.mode === 'dark';
   const backgroundColor = isDarkMode
    ? theme.palette.kemek.darkestGray
    : theme.palette.kemek.white;
   const textColor = isDarkMode
    ? theme.palette.kemek.white
    : theme.palette.kemek.darkestGray;
   const borderColor = isDarkMode
    ? theme.palette.kemek.darkGray
    : theme.palette.kemek.lightestGray;

   if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
   }

   tooltipEl.style.backgroundColor = backgroundColor;
   tooltipEl.style.color = textColor;
   tooltipEl.style.border = `1px solid ${borderColor}`;
   tooltipEl.style.fontFamily = theme.typography.fontFamily;

   if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map((b: any) => b.lines);

    const tableHead = document.createElement('thead');

    titleLines.forEach((title: any) => {
     const tr = document.createElement('tr');
     const th = document.createElement('th');
     th.style.borderWidth = '0';
     th.style.padding = '0 0 6px 0';
     th.style.textAlign = 'left';
     th.style.fontSize = '12px';
     th.style.fontWeight = '600';
     const text = document.createTextNode(title);

     th.appendChild(text);
     tr.appendChild(th);
     tableHead.appendChild(tr);
    });

    const tableBody = document.createElement('tbody');
    bodyLines.forEach((body: any, i: number) => {
     const colors = tooltip.labelColors[i];

     const span = document.createElement('span');
     span.style.backgroundColor = colors.backgroundColor;
     span.style.borderColor = colors.borderColor;
     span.style.borderWidth = '1px';
     span.style.borderStyle = 'solid';
     span.style.marginRight = '8px';
     span.style.height = '8px';
     span.style.width = '8px';
     span.style.borderRadius = '50%';
     span.style.display = 'inline-block';

     const tr = document.createElement('tr');
     tr.style.backgroundColor = 'inherit';
     tr.style.borderWidth = '0';

     const td = document.createElement('td');
     td.style.borderWidth = '0';
     td.style.padding = i === bodyLines.length - 1 ? '0' : '0 0 4px 0';
     td.style.fontSize = '12px';
     td.style.lineHeight = '16px';
     td.style.whiteSpace = 'nowrap';

     const text = document.createTextNode(body.join(' '));

     td.appendChild(span);
     td.appendChild(text);
     tr.appendChild(td);
     tableBody.appendChild(tr);
    });

    const tableRoot = tooltipEl.querySelector('table');

    while (tableRoot.firstChild) {
     tableRoot.firstChild.remove();
    }

    tableRoot.appendChild(tableHead);
    tableRoot.appendChild(tableBody);
   }

   const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

   tooltipEl.style.opacity = '1';
   tooltipEl.style.left = positionX + tooltip.caretX + 'px';
   tooltipEl.style.top = positionY + tooltip.caretY + 'px';
  },
  [getOrCreateTooltip, theme],
 );

 const options = useMemo(
  () => ({
   responsive: true,
   maintainAspectRatio: false,
   font: {
    family: theme.typography.fontFamily,
   },
   plugins: {
    legend: {
     position: 'bottom' as const,
     labels: {
      boxWidth: 12,
      padding: 8,
      font: {
       size: 12,
      },
     },
    },
    tooltip: {
     //  mode: 'index' as const,
     //  intersect: false,
     // Disable the on-canvas tooltip
     enabled: false,

     //   position: 'nearest',
     external: externalTooltipHandler,
    },
   },
   scales: {
    x: {
     display: true,
     grid: {
      display: false,
     },
    },
    y: {
     display: true,
     beginAtZero: true,
    },
   },
  }),
  [theme.typography.fontFamily],
 );

 return (
  <Box
   sx={{
    height: '100%',
    width: '100%',
    position: 'relative',
   }}
  >
   <Line data={chartData} options={options} />
  </Box>
 );
};

export default DashboardWidgetLineChart;

